import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import type { CoverMime } from '@/lib/patientReports/coverFile';
import { toPdfWinAnsi } from '@/lib/pdf/winAnsi';
import { buildPatientReportPdf, type PatientReportPdfLetterhead } from '@/lib/patientReports/pdf';
import { renderFilledDocxPdf } from '@/lib/patientReports/wordPdf';

export type ReportCoverFile = {
  bytes: Uint8Array;
  mime: CoverMime;
};

const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;
/** Same vertical inset as Clinio's own report pages. */
const MARGIN_Y = 48;
/** Side inset, about one third of the previous cover margin. */
const MARGIN_X = Math.round(MARGIN_Y / 3);

/**
 * One PDF for preview and for the archived report.
 * Order: cover sheet, then report pages. Letterhead is chrome on the report
 * pages only. A Word file, when present, supplies those report pages.
 */
export async function buildPatientReportDocument(input: {
  letterhead: PatientReportPdfLetterhead;
  coverFile?: ReportCoverFile | null;
  coverText?: { title: string; body: string } | null;
  docx?: Uint8Array | null;
  title: string;
  blocks: readonly {
    heading: string;
    body: string;
    pageBreakAfter?: boolean;
    headingStyle?: 'bold' | 'italic' | 'both';
    tocLines?: readonly { label: string; style?: 'bold' | 'italic' | 'both' }[];
    charts?: readonly { label: string; value: number; max: number }[];
  }[];
}): Promise<Uint8Array> {
  const textCover =
    input.coverText && (input.coverText.title.trim() || input.coverText.body.trim())
      ? input.coverText
      : null;
  const body = input.docx
    ? await renderFilledDocxPdf(input.docx, input.letterhead)
    : await buildPatientReportPdf({
        title: input.title,
        blocks: input.blocks,
        letterhead: input.letterhead,
        cover: input.coverFile ? null : textCover,
      });
  if (input.coverFile) return prependCoverFile(body, input.coverFile);
  if (input.docx && textCover) return prependTextCover(body, textCover);
  return body;
}

async function prependCoverFile(body: Uint8Array, cover: ReportCoverFile): Promise<Uint8Array> {
  // Keep the report pages in place. Copying them into a new file would
  // break the contents links, which point at those page objects.
  const pdf = await PDFDocument.load(body);
  if (cover.mime === 'application/pdf') {
    const source = await PDFDocument.load(cover.bytes);
    const copied = await pdf.copyPages(source, source.getPageIndices());
    for (let index = copied.length - 1; index >= 0; index -= 1) {
      const page = copied[index];
      if (page) pdf.insertPage(0, page);
    }
  } else {
    await addImageCover(pdf, cover.bytes);
  }
  return pdf.save();
}

async function prependTextCover(
  body: Uint8Array,
  cover: { title: string; body: string },
): Promise<Uint8Array> {
  const pdf = await PDFDocument.load(body);
  const page = pdf.insertPage(0, [PAGE_WIDTH, PAGE_HEIGHT]);
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  let y = PAGE_HEIGHT - MARGIN_Y;
  if (cover.title.trim()) {
    page.drawText(toPdfWinAnsi(cover.title.trim()).slice(0, 180), {
      x: MARGIN_X,
      y: y - 18,
      size: 18,
      font: bold,
      color: rgb(0.12, 0.14, 0.16),
    });
    y -= 32;
  }
  for (const paragraph of cover.body.split(/\n/)) {
    const line = toPdfWinAnsi(paragraph).slice(0, 110);
    if (!line) {
      y -= 8;
      continue;
    }
    if (y < MARGIN_Y) break;
    page.drawText(line, {
      x: MARGIN_X,
      y: y - 11,
      size: 11,
      font,
      color: rgb(0.12, 0.14, 0.16),
    });
    y -= 16;
  }
  return pdf.save();
}

async function addImageCover(pdf: PDFDocument, bytes: Uint8Array): Promise<void> {
  const png = bytes.length > 8 && bytes[0] === 0x89 && bytes[1] === 0x50;
  const image = png ? await pdf.embedPng(bytes) : await pdf.embedJpg(bytes);
  const page = pdf.insertPage(0, [PAGE_WIDTH, PAGE_HEIGHT]);
  const maxWidth = PAGE_WIDTH - MARGIN_X * 2;
  const maxHeight = PAGE_HEIGHT - MARGIN_Y * 2;
  const scale = Math.min(maxWidth / image.width, maxHeight / image.height);
  const width = image.width * scale;
  const height = image.height * scale;
  page.drawImage(image, {
    x: (PAGE_WIDTH - width) / 2,
    y: (PAGE_HEIGHT - height) / 2,
    width,
    height,
  });
}
